import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { useResetPassword } from '@/data/auth';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

const schema = z.object({
  password: z.string().min(8, 'Mindestens 8 Zeichen.'),
  confirm:  z.string(),
}).refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Passwörter stimmen nicht überein.' });
type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  useDocumentTitle('Passwort zurücksetzen');
  const { token } = useParams({ strict: false }) as { token: string };
  const navigate = useNavigate();
  const reset = useResetPassword();
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (values: FormValues) => {
    try {
      await reset.mutateAsync({ token, password: values.password });
      toast.success('Passwort gesetzt – du kannst dich jetzt einloggen.');
      void navigate({ to: '/login' });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reset fehlgeschlagen');
    }
  };

  return (
    <>
      <h1 className="text-2xl font-semibold">Neues Passwort setzen</h1>
      <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-1.5">
          <Label htmlFor="password">Neues Passwort</Label>
          <Input id="password" type="password" autoComplete="new-password" {...register('password')} />
          {errors.password && <p className="text-xs text-[var(--destructive)]">{errors.password.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Wiederholen</Label>
          <Input id="confirm" type="password" autoComplete="new-password" {...register('confirm')} />
          {errors.confirm && <p className="text-xs text-[var(--destructive)]">{errors.confirm.message}</p>}
        </div>
        <Button type="submit" className="w-full" disabled={reset.isPending}>
          {reset.isPending ? <Spinner /> : 'Passwort speichern'}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-[var(--muted-foreground)]">
        <Link to="/login" className="font-medium text-ocean-700 hover:underline">Zurück zum Login</Link>
      </p>
    </>
  );
}
